import { AxiosResponse } from 'axios';

import { HttpClient } from 'api/HttpClient';
import { ErrorDto } from 'errors/ErrorDto';
import { ErrorCodes } from 'enums/ErrorCodes';
import ResponseError from 'errors/common.errors';

import {
  CheckActiveApplicationsResponse,
  CreateBorrowerIntentRequest,
  CreateBorrowerIntentResponse,
  CompleteBorrowerRegistration,
  GetBorrowerShortInfoRequest,
  GetBorrowerShortInfoResponse,
  GetCoborrowerShortInfoBySsnRequest,
  GetCoborrowerShortInfoBySsnResponse,
  GetIntermediaryVerificationStatusRequest,
  GetIntermediaryVerificationStatusResponse,
  GetPhoneNumberLineTypeRequest,
  GetPhoneNumberLineTypeResponse,
  UpdateBorrowerMarketingConsentRequest,
  UpdateBorrowerMarketingConsentResponse,
  GetCompaniesResponseDto,
  GetIntermediariesSuggestionsDto,
  IntermediarySuggestionDto,
  CreateBorrowerRequest,
} from './preQualificationApi.types';
import { BorrowerDto } from '../BorrowerApi';

class PreQualificationApi extends HttpClient {
  public constructor() {
    super(process.env.REACT_APP_API_URL + '/prequalification' || '');
  }

  public async getPhoneNumberLineType(body: GetPhoneNumberLineTypeRequest) {
    const response = await this.instance
      .post(`/phoneNumber-line-type`, body)
      .then((res: AxiosResponse<GetPhoneNumberLineTypeResponse | ErrorDto>) => res.data);

    if ((response as ErrorDto)?.error?.errorCode === ErrorCodes.GET_USER_ELIGIBILITY_FAILED) {
      throw new ResponseError(response as ErrorDto);
    }

    return response as GetPhoneNumberLineTypeResponse;
  }

  public async getIntermediaryVerificationStatus({ id }: GetIntermediaryVerificationStatusRequest) {
    return this.instance
      .get(`/intermediaries/${encodeURIComponent(id)}/verification-status`)
      .then((response: AxiosResponse<GetIntermediaryVerificationStatusResponse>) => response.data);
  }

  public async getIntermediarySuggestions({ name }: GetIntermediariesSuggestionsDto) {
    return this.instance
      .get(`/intermediaries?name=${encodeURIComponent(name)}`)
      .then((response: AxiosResponse<IntermediarySuggestionDto[]>) => response.data);
  }

  public async checkActiveApplications() {
    return this.instance
      .get(`/applications/active-status`)
      .then((response: AxiosResponse<CheckActiveApplicationsResponse>) => response.data);
  }

  public async getBorrowerShortInfo(getBorrowerShortInfoRequest: GetBorrowerShortInfoRequest) {
    return this.instance
      .post(`/borrowers/short-info`, getBorrowerShortInfoRequest)
      .then((response: AxiosResponse<GetBorrowerShortInfoResponse>) => response.data);
  }

  public async getCoborrowerShortInfoBySsn(getCoborrowerShortInfoBySsnRequest: GetCoborrowerShortInfoBySsnRequest) {
    return this.instance
      .post(`/coborrowers/short-info/by-ssn`, getCoborrowerShortInfoBySsnRequest)
      .then((response: AxiosResponse<GetCoborrowerShortInfoBySsnResponse>) => response.data);
  }

  public async createBorrowerIntent(body: CreateBorrowerIntentRequest) {
    return this.instance
      .post(`/borrowers/intents`, body)
      .then((response: AxiosResponse<CreateBorrowerIntentResponse>) => response.data);
  }

  public async updateBorrowerMarketingConsent(body: UpdateBorrowerMarketingConsentRequest) {
    return this.instance
      .put(`/borrowers/marketing-consent`, body)
      .then((response: AxiosResponse<UpdateBorrowerMarketingConsentResponse | ErrorDto>) => response.data);
  }

  public async createBorrower(createBorrower: CreateBorrowerRequest) {
    return this.instance
      .post('/coborrowers', createBorrower)
      .then((response: AxiosResponse<BorrowerDto | ErrorDto>) => {
        return response.data;
      })
      .catch((error) => {
        throw error;
      });
  }

  public async completeBorrowerRegistration(createBorrower: CompleteBorrowerRegistration) {
    return this.instance
      .put('/borrowers/complete', createBorrower)
      .then((response: AxiosResponse<BorrowerDto | ErrorDto>) => {
        return response.data;
      })
      .catch((error) => {
        throw error;
      });
  }

  public async getCompanies(name: string) {
    return this.instance
      .get(`/employer-companies?name=${encodeURIComponent(name)}`)
      .then((response: AxiosResponse<GetCompaniesResponseDto>) => response.data);
  }
}

export const preQualificationApi = new PreQualificationApi();
